import { Checkbox } from "@/components/ui/checkbox";
import { ShieldAlert, Tag, Hash, Lock } from "lucide-react";

// Is this number the assembly's OWN identity (its primary/OEM)?
export function isIdentityNumber(n, primaryNorm, primaryValue) {
  if (!n) return false;
  if (n.owner === "truck_oem") return true;
  if (primaryNorm && n.normalized && n.normalized === primaryNorm) return true;
  if (primaryValue && n.value && String(n.value).toUpperCase() === String(primaryValue).toUpperCase()) return true;
  return false;
}

// LOCKED GUARDRAIL: the assembly's OEM number(s) AND its superseded number(s) are its
// permanent identity — they can NEVER move to a child (no override). Mirrors the donor tie.
export function isProtectedNumber(n, primaryNorm, primaryValue, supersededNorms = []) {
  if (isIdentityNumber(n, primaryNorm, primaryValue)) return true;
  if (n?.normalized && (supersededNorms || []).includes(n.normalized)) return true;
  return false;
}

// Shared reference-selection UI for the number/keyword hand-off (Phase A spin-off AND Split).
// Fully controlled: the caller owns the Sets + toggle callbacks so both entry points behave
// identically. Numbers always MOVE — one component, one record (his call, 4 Nov); only a keyword
// may stay on both. Protected identity numbers (OEM + superseded) are shown READ-ONLY.
export function ReferencePicker({
  numbers = [], keywords = [], primaryNorm = null, primaryValue = null, supersededNorms = [], prefix = "spinoff",
  sel, onToggleNum,
  selKw, onToggleKw, keepKw, onToggleKeepKw, lockGroup = null, ownGroup = null,
}) {
  const isProtected = (n) => isProtectedNumber(n, primaryNorm, primaryValue, supersededNorms);
  const componentNums = numbers.filter((n) => !isProtected(n));
  const protectedNums = numbers.filter(isProtected);
  // A BOLT-ON COMES OFF AS ITS WHOLE TAG (his rule, 3 Nov) — the tag's name and all its numbers, one tick for the tag
  // and no tick on a single number. The assembly's own numbers (its own plate, any loose number, and the tag answered
  // as the assembly's own) never leave it.
  const ownNums = componentNums.filter((n) => !n.group_id || n.group_id === ownGroup);
  const tagGroups = [];
  componentNums.filter((n) => n.group_id && n.group_id !== ownGroup && (!lockGroup || n.group_id === lockGroup)).forEach((n) => {
    let g = tagGroups.find((x) => x.key === n.group_id);
    if (!g) { g = { key: n.group_id, label: n.group_label || "A bolt-on's tag", rows: [] }; tagGroups.push(g); }
    g.rows.push(n);
  });

  const numberRow = (n) => (
    <div key={n.id} data-testid={`${prefix}-num-${n.id}`} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${sel.has(n.id) ? "border-[#E0781A] bg-orange-50" : "border-slate-200"}`}>
      <span className="font-mono font-bold">{n.value}</span>
      {n.group_primary && <span className="text-[10px] uppercase font-bold text-sky-700">this tag's part #</span>}
      {n.number_role && n.number_role !== "service_part" && <span className="text-[10px] text-slate-500">{n.number_role.replace("_", " ")}</span>}
      {n.brand && <span className="text-xs text-slate-500">{n.brand}</span>}
      {sel.has(n.id) && <span data-testid={`${prefix}-num-moves-${n.id}`} className="ml-auto text-[11px] text-slate-500">moves to the child</span>}
    </div>
  );

  return (
    <div className="space-y-3">
      {tagGroups.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" />{lockGroup ? "The tag that comes off — all of it" : "Bolt-on tags — each comes off whole"}</div>
          {tagGroups.map((g, gi) => {
            const ids = g.rows.map((n) => n.id);
            const all = ids.every((id) => sel.has(id));
            return (
              <div key={g.key} data-testid={`${prefix}-group-${gi}`} className="rounded-lg border border-slate-200">
                <label className={`flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 ${lockGroup ? "" : "cursor-pointer"}`}>
                  <Checkbox data-testid={`${prefix}-group-check-${gi}`} checked={all} disabled={!!lockGroup}
                    onCheckedChange={() => ids.forEach((id) => (all ? sel.has(id) : !sel.has(id)) && onToggleNum(id))}
                    className="border-slate-300" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{g.label}</span>
                  <span className="text-[10px] text-slate-400">{g.rows.length} value{g.rows.length === 1 ? "" : "s"} — the whole tag goes</span>
                </label>
                <div className="p-1.5 space-y-1.5">{g.rows.map(numberRow)}</div>
              </div>
            );
          })}
        </div>
      )}

      {!lockGroup && ownNums.length > 0 && (
        <div className="space-y-1.5" data-testid={`${prefix}-own`}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />The assembly's own numbers — they stay with it</div>
          <div className="flex flex-wrap gap-1.5">
            {ownNums.map((n) => (
              <span key={n.id} data-testid={`${prefix}-own-num-${n.id}`} className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50/60 px-2 py-1 font-mono text-xs font-bold text-slate-700">
                <Lock className="w-3 h-3 text-amber-700" />{n.value}
              </span>
            ))}
          </div>
        </div>
      )}

      {!lockGroup && protectedNums.length > 0 && (
        <div className="space-y-1.5" data-testid={`${prefix}-protected`}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />Assembly's OEM / superseded number(s) — locked, never move</div>
          <div className="flex items-start gap-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
            <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            These are the assembly's permanent identity (like the donor tie). They stay on the assembly and can never move to a child.
          </div>
          {protectedNums.map((n) => (
            <div key={n.id} data-testid={`${prefix}-protected-num-${n.id}`} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-2.5 py-2 opacity-90">
              <Lock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="font-mono font-bold">{n.value}</span>
              <span className="text-[10px] uppercase font-bold text-emerald-700">{n.owner === "truck_oem" || (primaryNorm && n.normalized === primaryNorm) || (primaryValue && String(n.value).toUpperCase() === String(primaryValue).toUpperCase()) ? "OEM" : "superseded"}</span>
            </div>
          ))}
        </div>
      )}

      {keywords.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />Keywords to move</div>
          <div className="flex flex-wrap gap-1.5">
            {keywords.map((k) => (
              <button key={k} type="button" data-testid={`${prefix}-kw-${k}`} onClick={() => onToggleKw(k)}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold border transition-colors ${selKw.has(k) ? "border-[#E0781A] bg-orange-50 text-[#B45309]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                <Tag className="w-3 h-3" />{k}
              </button>
            ))}
          </div>
          {[...selKw].length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {[...selKw].map((k) => (
                <label key={k} className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
                  <Checkbox data-testid={`${prefix}-kw-keep-${k}`} checked={keepKw.has(k)} onCheckedChange={() => onToggleKeepKw(k)} className="border-slate-300 h-3.5 w-3.5" />
                  keep "{k}" on both
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Build the {move_*, copy_*} payload arrays from the selection Sets — one source of truth
// used by BOTH spin-off and split, so the hand-off is assembled identically. Numbers never copy.
export function buildHandoffPayload(sel, selKw, keepKw) {
  return {
    move_number_ids: [...sel],
    move_keywords: [...selKw].filter((k) => !keepKw.has(k)),
    copy_keywords: [...selKw].filter((k) => keepKw.has(k)),
  };
}
